import { useTheme } from '@mui/material';
import type { TFunction } from 'i18next';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, PAGES_FILTERS, STORAGE } from '@/constants';
import { assetCategory } from '@/enums';
import type { ActiveFiltersType, Assets, FilterTagType, IFilters, IScene, IUser, PagesType, SearchProps, ValidValue } from '@/types';

/**
 * Full name: new line to "br"
 * It splits a text by lines and insert "br" tag
 * Variation to work with react
 */
export const nl2br = (text: string) =>
    text.split('\n').map((line: string, i) =>
        i === 0 ? (
            line
        ) : (
            <>
                <br />
                {line}
            </>
        ),
    );

// age string validation
export const validationAge = (age: string) => {
    const { t } = useTranslation();
    let resultString = '';
    const count = +age % 10;
    if (age?.length > 0 && age.includes('-')) {
        resultString = `${age} ${t('characters.age').toLowerCase()}`;
    } else {
        if (+age > 9 && +age < 21) {
            resultString = `${age} ${t('characters.age').toLowerCase()}`;
        } else {
            if (count === 1) {
                resultString = `${age} ${t('characters.year').toLowerCase()}`;
            } else if (count > 1 && count < 5) {
                resultString = `${age} ${t('characters.years').toLowerCase()}`;
            } else {
                resultString = `${age} ${t('characters.age').toLowerCase()}`;
            }
        }
    }
    return resultString;
};

// year input check
export const verificationAgeText = (age: string) => {
    let result = false;
    const count = age.split('-');
    if (!age.includes('-')) {
        //check that the input consists of 1 number with a length of no more than 4 characters
        result = !!(Number.isInteger(+age) && age.length < 5);
    } else {
        const start = count[0] ?? '';
        const end = count[1] ?? '';
        result = !!(
            (
                Number.isInteger(+start) &&
                Number.isInteger(+end) &&
                start.length &&
                start.length < 5 && //maximum number length 4 characters
                end.length &&
                end.length < 5 && //maximum number length 4 characters
                +start < +end
            ) //start number must be less than end number
        );
    }
    return result;
};

// to get the main photo path
export const getImagePath = (images: Assets[], imageIndex: number, src?: string) => {
    const theme = useTheme();
    if (images.length > imageIndex && images[imageIndex]?.category === assetCategory.image) return `${STORAGE + images[imageIndex].path}`;
    else return src ? `${src}-${theme.palette.mode === 'dark' ? 'dark' : 'light'}.svg` : null;
};

export const focusField = (el: HTMLInputElement | HTMLTextAreaElement) => {
    const pos = el.value.length;
    el.focus();
    el.setSelectionRange(pos, pos);
};

export const BooleanFilter = <T,>(x: T): x is ValidValue<T> => Boolean(x);

// in some places it can be multiple scenes with the same ID in one list
// this is when presentation goes by line and then drag&drop is in action
// to avoid confusing react with multiple keys, we need to include sequenceId of the intersection episode/line
export enum SCENE_KEY {
    ONE = 1,
    MANY = 2,
}

export const getSceneKey = (scene: IScene, keyType: SCENE_KEY = SCENE_KEY.ONE) => {
    if (keyType === SCENE_KEY.MANY) {
        if (!('sequenceId' in scene)) {
            throw new Error('sequenceId must be included in "scene" if multiple scenes are going to be shown in the list');
        }

        return [scene.sequenceId, scene.id].join('/');
    }

    return scene.id;
};

export const getUserName = (user: IUser) => [user.first_name, user.last_name].filter(BooleanFilter).join(' ');

// returns a database entity with sorted keys
export const stringifyEntity = (entity: object) => JSON.stringify(entity, Object.keys(entity).sort());

export const getFlatFilters = (filtersList: Partial<ActiveFiltersType> | IFilters) => Object.values(filtersList).flat(1);

// Flat tag ids for backend requests. Relationships are AND-semantics and
// client-side only (#1839): sending them as `tags[]` would OR-widen the result.
export const getServerFilterTags = (activeFilters: ActiveFiltersType) => {
    const { relationships: _relationships, ...serverFilters } = activeFilters;
    return getFlatFilters(serverFilters);
};

export const getPageActiveFilters = (page: PagesType, activeFilters: ActiveFiltersType) => {
    const cloneAllActiveFilters = structuredClone(activeFilters);

    const activeFiltersList = {} as ActiveFiltersType;

    for (const key in cloneAllActiveFilters) {
        if (PAGES_FILTERS[page].includes(key)) {
            activeFiltersList[key as keyof ActiveFiltersType] = cloneAllActiveFilters[key as keyof ActiveFiltersType];
        }
    }

    return activeFiltersList;
};

export const prepareTagsForRequest = (page: PagesType, activeFilters: ActiveFiltersType) => {
    const params = new URLSearchParams();
    const activeFiltersList: Partial<ActiveFiltersType> = getPageActiveFilters(page, activeFilters);

    // AND-semantics, applied client-side only (#1839)
    delete activeFiltersList.relationships;

    const episodesTags = activeFiltersList.episodes?.length ? activeFiltersList.episodes : '';

    if (activeFiltersList.episodes?.length) {
        delete activeFiltersList.episodes;
    }

    switch (page) {
        case 'lines': {
            if (activeFiltersList.characters?.length) {
                const charactersTags = activeFiltersList.characters;
                charactersTags.forEach(characterId => {
                    params.append('characters_tags[]', String(characterId));
                });
                delete activeFiltersList.characters;
            }

            const tags = getFlatFilters(activeFiltersList);
            tags.forEach(tag => {
                params.append('tags[]', String(tag));
            });

            if (episodesTags) {
                episodesTags.forEach(episodeId => {
                    params.append('episode_id[]', String(episodeId));
                });
            }

            return params.toString();
        }
        case 'episodes':
        case 'planEpisode':
        case 'planLine': {
            const tags = getFlatFilters(activeFiltersList);
            tags.forEach(tag => {
                params.append('tags[]', String(tag));
            });

            return params.toString();
        }

        case 'characters': {
            const lineTags = activeFiltersList.lines?.length ? activeFiltersList.lines : '';

            if (lineTags) {
                lineTags.forEach(lineId => {
                    params.append('line_tag_id[]', String(lineId));
                });
                delete activeFiltersList.lines;
            }

            getFlatFilters(activeFiltersList).forEach(tag => {
                params.append('scenes_tags[]', String(tag));
            });

            if (episodesTags) {
                episodesTags.forEach(episodeId => {
                    params.append('scenes_episode_id[]', String(episodeId));
                });
            }

            return params.toString();
        }

        case 'locations': {
            getFlatFilters(activeFiltersList).forEach(tag => {
                params.append('scenes_tags[]', String(tag));
            });

            if (episodesTags) {
                episodesTags.forEach(episodeId => {
                    params.append('scenes_episode_id[]', String(episodeId));
                });
            }

            return params.toString();
        }

        default:
            return '';
    }
};

export const getFilterTagLabel = (tag: FilterTagType) => {
    if ('name' in tag && tag.name) {
        return tag.name;
    } else if ('title' in tag && tag.title) {
        return tag.title;
    } else if ('entity_tag' in tag && tag.entity_tag) {
        return tag.entity_tag.name;
    }
    return '';
};

// Entity tags store named color codes (colorA…colorJ, colorX = none), not hexes
export const resolveTagColorCode = (code: string | undefined) => {
    if (!code) return undefined;
    if (code.startsWith('#')) return code;
    return COLORS.find(c => c.code === code)?.color;
};

export const getFilterTagColor = (tag: FilterTagType) =>
    resolveTagColorCode('entity_tag' in tag && tag.entity_tag ? tag.entity_tag.color : undefined);

export const getFilterTagId = (tag: FilterTagType) => {
    if ('entity_tag' in tag && tag.entity_tag) {
        return tag.entity_tag.id;
    } else if ('id' in tag && tag.id) {
        return tag.id;
    }
    return 0;
};

//replace search text
export const textSearchReplace = (text: string) =>
    text
        .replace(/\\/g, '\\\\')
        .replace(/\*/g, '\\*')
        .replace(/\+/g, '\\+')
        .replace(/\)/g, '\\)')
        .replace(/\(/g, '\\(')
        .replace(/\?/g, '\\?')
        .replace(/\?/g, '\\?')
        .replace(/\[/g, '\\[')
        .replace(/\]/g, '\\]');

const escapeHtml = (str: string) =>
    str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export const searchMatches = ({ searchText, str, indexStep, scrollStep }: SearchProps) => {
    const textReplace = textSearchReplace(searchText);
    const regexp = new RegExp(textReplace, 'ig');
    const matchValue = str.match(regexp);

    return !searchText
        ? str
        : !matchValue
          ? str
          : str
                .split(regexp)
                .map((el, index, array) => {
                    if (index < array.length - 1) {
                        matchValue.shift();
                        return `${escapeHtml(el)}<mark class=${
                            indexStep && index === indexStep - 1 ? 'searchActive' : 'searchNotActive'
                        } data-type="search" id=${scrollStep ? `box${scrollStep + index + 1}` : `box${index + 1}`}>${escapeHtml(searchText)}</mark>`;
                    }
                    return escapeHtml(el);
                })
                .join('');
};

export const wordsCap = (text: string) => {
    if (text.length > 0) {
        const firstChar = text[0] ?? '';
        return text.replace(/^[a-zA-Zа-яА-Я]/, firstChar.toUpperCase()).replaceAll(/\.\s?[a-zA-Zа-яА-Я]/g, (s: string) => s.toUpperCase());
    }
    return text;
};

export const notHighlightAddedText = (reactEvent: React.FormEvent<HTMLDivElement>) => {
    const event = reactEvent.nativeEvent as InputEvent;
    const selection = window.getSelection();

    if (event.inputType !== 'insertText') {
        // it may be an Enter or deleteContentBackward, we don't care about these
        return;
    }
    if (selection?.focusNode?.parentElement?.nodeName === 'MARK') {
        if (selection.anchorOffset === selection?.focusNode?.parentElement.textContent?.length) {
            const { parentElement } = selection.focusNode;
            if (parentElement.textContent) {
                parentElement.textContent = parentElement.textContent.slice(0, parentElement.textContent.length - 1);
            } else {
                return;
            }

            let updatedTextNode: ChildNode | Text;
            if (selection.focusNode.nextSibling) {
                // before mark
                selection.focusNode.nextSibling.nodeValue = `${event.data ?? ''}${selection?.focusNode.nextSibling.nodeValue ?? ''}`;
                updatedTextNode = selection.focusNode.nextSibling;
            } else {
                //after mark
                const textNode = document.createTextNode('');
                textNode.nodeValue = event.data ?? '';
                (event.target as HTMLDivElement).appendChild(textNode);
                updatedTextNode = textNode;
            }
            const range = document.createRange();
            range.setStart(updatedTextNode, 1);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }
};

export const getErrorMessageCharacters = (message: string, t: TFunction) => {
    switch (message) {
        case 'This value has already been taken.':
            return t('characters.error-name');

        case 'The name field is required.':
            return t('characters.error-name-required');

        default:
            return message;
    }
};
export const pasteClearText = (event: React.ClipboardEvent<HTMLElement>, maxLength?: number) => {
    const content = event.clipboardData.getData('text/plain');
    const clearTagContent = content.replace(/(<([^>]+)>)/gi, '').slice(0, maxLength);
    document.execCommand('insertText', false, clearTagContent);
};
export const blockOverflow = (ref: RefObject<HTMLDivElement>) => {
    const row = ref.current?.parentElement;
    const card = ref.current;

    if (row && card) {
        const rowSize = row.clientWidth;
        const cardSize = card.clientWidth;
        const cardOffset = card.getBoundingClientRect().left - row.getBoundingClientRect().left;
        return cardOffset + cardSize > rowSize;
    }

    return false;
};
export function convertSecondsToHMS(seconds: number) {
    const formatted = new Date(seconds * 1000).toISOString().slice(11, 19);
    const timings = formatted.split(':').filter(el => el !== '00');
    if (timings.length <= 2) {
        return formatted.split(':').slice(-2).join(':');
    }
    return timings.join(':');
}
export const scrollToSceneID = (scene_id: number) => {
    const element = document.querySelector(`.class-${scene_id}`);

    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
        });
    }
};
export const scrollToSceneIDPaper = (scene_id: number) => {
    const element = document.querySelector(`.class-${scene_id}-paper`);

    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
        });
    }
};

export const dragReorder = <T,>(list: T[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    if (removed !== undefined) {
        result.splice(endIndex, 0, removed);
    }
    return result;
};
